import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, Pencil, RotateCcw, Pause, Play } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';

export function RecurringTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({
    project_id: '', frequency: 'monthly', generation_day: 1, net_terms: 30,
    notes: '', po_reference: '', line_items: [],
  });

  const fetchTemplates = useCallback(() => {
    api.get('/recurring-invoice-templates').then(r => { setTemplates(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTemplates();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, [fetchTemplates]);

  const loadSections = async (projectId) => {
    if (!projectId) { setSections([]); return; }
    try {
      const proj = await api.get(`/projects/${projectId}`);
      setSections(proj.data.sections || []);
    } catch { setSections([]); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ project_id: '', frequency: 'monthly', generation_day: 1, net_terms: 30, notes: '', po_reference: '', line_items: [] });
    setSections([]);
    setSheetOpen(true);
  };

  const openEdit = async (tmpl) => {
    setEditing(tmpl);
    setForm({
      project_id: tmpl.project_id || '',
      frequency: tmpl.frequency || 'monthly',
      generation_day: tmpl.generation_day || 1,
      net_terms: tmpl.net_terms || 30,
      notes: tmpl.notes || '',
      po_reference: tmpl.po_reference || '',
      line_items: tmpl.line_items || [],
    });
    if (tmpl.project_id) await loadSections(tmpl.project_id);
    setSheetOpen(true);
  };

  const addLineItem = () => {
    setForm(f => ({
      ...f,
      line_items: [...f.line_items, { id: Date.now().toString(), contract_section_id: '', section_name: '', section_number: '', amount: 0, work_description: '', display_name: '' }],
    }));
  };

  const updateLineItem = (idx, field, value) => {
    setForm(f => ({
      ...f,
      line_items: f.line_items.map((li, i) => {
        if (i !== idx) return li;
        const updated = { ...li, [field]: value };
        if (field === 'contract_section_id') {
          const sec = sections.find(s => s.id === value);
          if (sec) {
            updated.section_name = sec.section_name || '';
            updated.section_number = sec.section_number || '';
          }
        }
        return updated;
      }),
    }));
  };

  const removeLineItem = (idx) => {
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.project_id) return toast.error('Project is required');
    if (form.line_items.length === 0) return toast.error('Add at least one line item');
    const invalidItems = form.line_items.filter(li => !li.amount || li.amount <= 0);
    if (invalidItems.length > 0) return toast.error('All line items must have a positive amount');

    try {
      if (editing) {
        await api.put(`/recurring-invoice-templates/${editing.id}`, form);
        toast.success('Template updated');
      } else {
        await api.post('/recurring-invoice-templates', form);
        toast.success('Recurring template created');
      }
      setSheetOpen(false);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving template'); }
  };

  const toggleActive = async (tmpl) => {
    try {
      await api.put(`/recurring-invoice-templates/${tmpl.id}`, { is_active: !tmpl.is_active });
      toast.success(tmpl.is_active ? 'Template paused' : 'Template activated');
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteTemplate = async (tmpl) => {
    if (!window.confirm(`Delete recurring template for ${tmpl.project_number} - ${tmpl.project_name}?`)) return;
    try {
      await api.delete(`/recurring-invoice-templates/${tmpl.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const subtotal = form.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);

  return (
    <div className="space-y-4" data-testid="recurring-templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-lg">Recurring Templates</h2>
          <p className="text-xs text-muted-foreground">Auto-generate draft invoices on a schedule</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-1" /> New Template
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>
      ) : templates.length === 0 ? (
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-8 text-center">
            <RotateCcw className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-sm text-muted-foreground">No recurring invoice templates yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Create a template to auto-generate monthly or quarterly invoices.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {templates.map(tmpl => (
            <Card key={tmpl.id} className={`border-border bg-card shadow-none transition-colors ${!tmpl.is_active ? 'opacity-50' : ''}`} data-testid={`template-card-${tmpl.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{tmpl.project_number} - {tmpl.project_name}</p>
                      <Badge variant="outline" className="text-xs">{tmpl.client_name}</Badge>
                      <Badge className={`text-xs ${tmpl.is_active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
                        {tmpl.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>{tmpl.frequency === 'monthly' ? 'Monthly' : 'Quarterly'}</span>
                      <span>Day {tmpl.generation_day}</span>
                      <span>{tmpl.net_terms === -1 ? 'Pay when Paid' : tmpl.net_terms === 0 ? 'Upon Receipt' : `Net ${tmpl.net_terms}`}</span>
                      <span>{tmpl.line_items?.length || 0} line item{(tmpl.line_items?.length || 0) !== 1 ? 's' : ''}</span>
                      <span className="font-medium text-primary">${(tmpl.line_items || []).reduce((s, li) => s + (li.amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEdit(tmpl)} data-testid={`edit-template-${tmpl.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className={`h-7 w-7 ${tmpl.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}`} onClick={() => toggleActive(tmpl)} data-testid={`toggle-template-${tmpl.id}`}>
                      {tmpl.is_active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deleteTemplate(tmpl)} data-testid={`delete-template-${tmpl.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Template Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="template-form-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">{editing ? 'Edit Template' : 'New Recurring Template'}</SheetTitle>
            <SheetDescription>Configure auto-generated invoices for a project.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            {!editing && (
              <div>
                <Label className="text-xs text-muted-foreground">Project *</Label>
                <SearchableSelect
                  value={form.project_id || ''}
                  onValueChange={v => { setForm(f => ({ ...f, project_id: v, line_items: [] })); loadSections(v); }}
                  options={projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))}
                  placeholder="Select project"
                  searchPlaceholder="Search projects..."
                  data-testid="template-project"
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Frequency</Label>
                <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v }))}>
                  <SelectTrigger className="bg-background border-border" data-testid="template-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Generate on Day</Label>
                <Input type="number" min={1} max={28} value={form.generation_day} onChange={e => setForm(f => ({ ...f, generation_day: parseInt(e.target.value) || 1 }))} className="bg-background border-border" data-testid="template-gen-day" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Net Terms</Label>
                <Select value={String(form.net_terms)} onValueChange={v => setForm(f => ({ ...f, net_terms: parseInt(v) }))}>
                  <SelectTrigger className="bg-background border-border" data-testid="template-net-terms"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="0">Upon Receipt</SelectItem>
                    <SelectItem value="-1">Pay when Paid</SelectItem>
                    <SelectItem value="15">Net 15</SelectItem>
                    <SelectItem value="30">Net 30</SelectItem>
                    <SelectItem value="45">Net 45</SelectItem>
                    <SelectItem value="60">Net 60</SelectItem>
                    <SelectItem value="90">Net 90</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">P.O. / Reference</Label>
              <Input value={form.po_reference} onChange={e => setForm(f => ({ ...f, po_reference: e.target.value }))} className="bg-background border-border" placeholder="Optional" data-testid="template-po-ref" />
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <RichTextEditor value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} placeholder="Optional notes for generated invoices" collapsedHeight={80} />
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Line Items</Label>
                <Button size="sm" variant="outline" onClick={addLineItem} disabled={!form.project_id} data-testid="template-add-line-item">
                  <Plus className="h-3 w-3 mr-1" /> Add Line
                </Button>
              </div>

              {form.line_items.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-border rounded-md">
                  {form.project_id ? 'Add line items for the recurring invoice.' : 'Select a project first.'}
                </p>
              ) : (
                form.line_items.map((li, idx) => (
                  <Card key={li.id || idx} className="border-border bg-background shadow-none">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          {sections.length > 0 ? (
                            <Select value={li.contract_section_id || ''} onValueChange={v => updateLineItem(idx, 'contract_section_id', v)}>
                              <SelectTrigger className="bg-background border-border text-xs h-8"><SelectValue placeholder="Select section" /></SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {sections.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.section_number ? `${s.section_number} - ` : ''}{s.section_name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input placeholder="Line item name" value={li.display_name || li.section_name} onChange={e => updateLineItem(idx, 'display_name', e.target.value)} className="bg-background border-border h-8 text-xs" />
                          )}
                        </div>
                        <Input type="number" step="0.01" min="0" value={li.amount || ''} onChange={e => updateLineItem(idx, 'amount', parseFloat(e.target.value) || 0)} className="w-28 bg-background border-border h-8 text-xs text-right" placeholder="Amount" data-testid={`template-line-amount-${idx}`} />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={() => removeLineItem(idx)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <RichTextEditor placeholder="Work description (for PDF)" value={li.work_description || ''} onChange={v => updateLineItem(idx, 'work_description', v)} collapsedHeight={60} />
                    </CardContent>
                  </Card>
                ))
              )}

              {form.line_items.length > 0 && (
                <div className="text-right text-sm font-heading font-bold text-primary">
                  Template Total: ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              )}
            </div>

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground" data-testid="template-save-btn">
              {editing ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
